import React, { useEffect } from "react";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import {
  RangeCalendarIcon,
  NumberedCalendarIcon,
} from "./PageTableDateFilterIcons";
import DatePicker from "react-datepicker";
import { globalDateFormat1 } from "./PageFormats";
import "react-datepicker/dist/react-datepicker.css";

export default function PageTableDateFilters({
  range,
  setRange,
  PageName,
  calendarRef,
  activeFilter,
  showCalendar,
  setActiveFilter,
  setShowCalendar,
  handleCustomDateSubmit,
}) {
  // Hook for reading/updating ?datefilter=
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!range.from && !range.to) {
      const currentDate = new Date();
      const threeDaysLater = dayjs(currentDate).add(3, "day").toDate();
      setRange({ from: currentDate, to: threeDaysLater });
    }
  }, [range, setRange]);

  // ─── Sync URL with current selection ───────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("datefilter", activeFilter);

    if (activeFilter === "Custom" && range?.from && range?.to) {
      params.set("from", dayjs(range.from).format("YYYY-MM-DD"));
      params.set("to", dayjs(range.to).format("YYYY-MM-DD"));
    } else {
      params.delete("from");
      params.delete("to");
    }

    // Only update if something actually changed to avoid loops
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, range]);
  return (
    <div className="flex items-center mb-3 space-x-6">
      <>
        <div
          className={`px-4 py-2 space-x-4 rounded-lg shadow-sm bg-sky-200 ${
            activeFilter === "Currentday" ? "border-2 border-black" : ""
          }`}
        >
          <button
            className="px-3 py-1 text-sm font-semibold text-black rounded"
            onClick={() => setActiveFilter("Currentday")}
          >
            Current Day
          </button>
        </div>
        {PageName !== "CompletedCalls" ? (
          <NumberedCalendarIcon
            day={3}
            size={40}
            onClick={() => setActiveFilter("Threeday")}
            className={
              activeFilter === "Threeday"
                ? "border-2 border-black rounded-md"
                : ""
            }
          />
        ) : null}
        <RangeCalendarIcon
          size={40}
          onClick={() => {
            setShowCalendar((prev) => !prev);
            setRange({ from: undefined, to: undefined });
          }}
          className={
            activeFilter === "Custom" ? "border-2 border-black rounded-md" : ""
          }
        />

        {showCalendar && (
          <div
            ref={calendarRef}
            className="absolute z-20 p-4 mt-2 bg-white border border-gray-300 rounded shadow-lg"
          >
            <div className="space-y-3">
              {/* Date Filters and Submit */}
              <div className="flex items-center px-4 py-3 space-x-2 bg-gray-500 rounded-lg shadow">
                <label className="font-medium">Date:</label>
                <label>From</label>
                <DatePicker
                  selected={range.from}
                  onChange={(date) =>
                    setRange((prev) => ({ ...prev, from: date }))
                  }
                  onChangeRaw={(e) => e.preventDefault()}
                  dateFormat="MM/dd/yyyy"
                  placeholderText={globalDateFormat1()}
                  className="w-32 px-2 py-1 border rounded"
                />
                <label>To</label>
                <DatePicker
                  selected={range.to}
                  onChange={(date) =>
                    setRange((prev) => ({ ...prev, to: date }))
                  }
                  onChangeRaw={(e) => e.preventDefault()}
                  dateFormat="MM/dd/yyyy"
                  placeholderText={globalDateFormat1()}
                  className="w-32 px-2 py-1 border rounded"
                />
                {/*
                  Add validation: "To" date must not be earlier than "From" date.
                  Disable button and prevent submission if invalid.
                */}
                {(() => {
                  const isRangeValid =
                    range.from && range.to && range.to >= range.from;
                  return (
                    <>
                      <div className="flex flex-col items-start space-y-1">
                        <button
                          onClick={() => handleCustomDateSubmit()}
                          className={`px-4 py-1 text-white rounded ${
                            isRangeValid
                              ? "bg-sky-600"
                              : "bg-gray-400 cursor-not-allowed"
                          }`}
                          disabled={!isRangeValid}
                        >
                          Submit
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
            {(() => {
              const isRangeValid =
                range.from && range.to && range.to >= range.from;
              return (
                <>
                  {!isRangeValid && (
                    <p className="pt-2 text-sm text-red-600">
                      "To" date cannot be earlier than "From" date.
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </>
    </div>
  );
}
